import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import * as notificationService from "../../services/notification.services";
import { userList } from "../../services/user.service";
import CustomPagination from "../../common/custompagination";
import Sorting from "../../common/sorting";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";

const NotificationTable = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState(false);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [globalsearch, setGlobalSearch] = useState("");
  const [search, setSearch] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [recipientMode, setRecipientMode] = useState("selected");
  const [userSearch, setUserSearch] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [counts, setCounts] = useState({
    unread_notification: 0,
    total_records: 0,
  });

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Notifications"), url: "" },
  ];

  const loadCounts = () => {
    notificationService
      .Count()
      .then((response) => {
        setCounts(
          response?.data || { unread_notification: 0, total_records: datalength },
        );
      })
      .catch(() => {
        setCounts({ unread_notification: 0, total_records: datalength });
      });
  };

  useEffect(() => {
    const getData = setTimeout(() => {
      setLoader(true);
      const formData = new FormData();
      formData.append("page", params.pgno);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);

      notificationService
        .List(formData)
        .then((data) => {
          setDataLength(data?.data?.total_records || 0);
          setList(data?.data?.list || []);
          setPage(data?.data?.page || 1);
          setLoader(false);
          loadCounts();
        })
        .catch((error) => {
          console.log("error ====> ", error);
          setLoader(false);
        });
    }, 300);

    return () => clearTimeout(getData);
  }, [location, statsupdate, sorting, search, globalsearch, itemperpage, params.pgno]);

  useEffect(() => {
    const formData = new FormData();
    formData.append("page", 1);
    formData.append("per_page", 1000);
    formData.append("sort", JSON.stringify({ order: 1, column: "name" }));
    formData.append("search", JSON.stringify({}));
    formData.append("global_search", "");

    userList(formData)
      .then((response) => {
        setUserOptions(response?.data?.list || []);
      })
      .catch((error) => {
        console.log("userListError", error);
      });
  }, []);

  const sendValidationSchema = Yup.object().shape({
    title: Yup.string().trim().required(t("Title is required")),
    description: Yup.string().trim(),
  });

  const sendFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_ids: [],
      title: "",
      description: "",
    },
    validationSchema: sendValidationSchema,
    onSubmit: (values, { resetForm }) => {
      const selectedIds =
        recipientMode === "all"
          ? userOptions.map((user) => user?._id).filter(Boolean)
          : values.user_ids;

      if (!selectedIds.length) {
        Swal.fire({
          icon: "error",
          text: t("Please select at least one user"),
          ...SWAL_SETTINGS,
        });
        return;
      }

      setSendLoading(true);

      const formData = new FormData();
      formData.append("user_ids", JSON.stringify(selectedIds));
      formData.append("title", values.title.trim());
      formData.append("description", values.description.trim());

      notificationService
        .SendToUsers(formData)
        .then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            resetForm();
            setUserSearch("");
            setStatusUpdate((prev) => !prev);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        })
        .finally(() => {
          setSendLoading(false);
        });
    },
  });

  const markAsRead = (id) => {
    notificationService
      .MarkRead(id)
      .then((response) => {
        if (response.success) {
          setStatusUpdate((prev) => !prev);
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch((error) => {
        console.log("markReadError", error);
      });
  };

  const markAllAsRead = () => {
    notificationService
      .MarkAllRead()
      .then((response) => {
        if (response.success) {
          setStatusUpdate((prev) => !prev);
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch((error) => {
        console.log("markAllReadError", error);
      });
  };

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    const sort = { order: e.target.classList.contains("assc") ? -1 : 1, column };
    setSorting(sort);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    setSearch({});
  };

  const handleMultiUserChange = (e) => {
    const selectedValues = Array.from(e.target.selectedOptions, (option) => option.value);
    sendFormik.setFieldValue("user_ids", selectedValues);
    sendFormik.setFieldTouched("user_ids", true, false);
  };

  const filteredUsers = userOptions.filter((user) => {
    const keyword = userSearch.trim().toLowerCase();
    if (!keyword) return true;
    const name = (user?.name || "").toLowerCase();
    const email = (user?.email || "").toLowerCase();
    const phone = (user?.phone_number || "").toLowerCase();
    return name.includes(keyword) || email.includes(keyword) || phone.includes(keyword);
  });

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleString();
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">{t("Notifications")}</h6>
              {/* <div className="d-flex align-items-center">
                <span className="badge badge-primary me-2">
                  {t("Total")}: {counts?.total_records || 0}
                </span>
                <span className="badge badge-warning me-3">
                  {t("Unread")}: {counts?.unread_notification || 0}
                </span>
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      placeholder={t("input_placeholder_search")}
                      onChange={(e) => setGlobalSearch(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="btn btn-primary mr-2"
                >
                  {t("Mark all read")}
                </button>
                <button
                  type="reset"
                  value="Reset"
                  onClick={resetFilter}
                  className="btn btn-warning"
                >
                  {t("btn_reset_filter")}
                </button>
              </div> */}
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h6 className="mb-3">{t("Send Notification")}</h6>
                <form onSubmit={sendFormik.handleSubmit}>
                  <div className="row">
                    <div className="col-md-12 form-group">
                      <label className="text-left d-flex">
                        {t("Recipients")} <span className="requirestar">*</span>
                      </label>
                      <div className="d-flex flex-wrap gap-4 mt-2">
                        <label className="d-inline-flex align-items-center me-4">
                          <input
                            type="radio"
                            name="recipientMode"
                            checked={recipientMode === "selected"}
                            onChange={() => setRecipientMode("selected")}
                            className="me-2"
                          />
                          {t("Selected users")}
                        </label>
                        <label className="d-inline-flex align-items-center">
                          <input
                            type="radio"
                            name="recipientMode"
                            checked={recipientMode === "all"}
                            onChange={() => setRecipientMode("all")}
                            className="me-2"
                          />
                          {t("All users")}
                        </label>
                      </div>
                    </div>

                    {recipientMode === "all" ? (
                      <div className="col-md-12 form-group">
                        <div className="alert alert-warning mb-0">
                          {t("The same notification will be delivered to every user account.")}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="col-md-12 form-group">
                          <label className="text-left d-flex">{t("Search Users")}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder={t("Type name, email")}
                          />
                          <small className="text-muted d-flex mt-1">
                            {t("Pick one or more users below.")}
                          </small>
                        </div>

                        <div className="col-md-12 form-group">
                          <label className="text-left d-flex">
                            {t("Users")} <span className="requirestar">*</span>
                          </label>
                          <select
                            className="form-control"
                            multiple
                            name="user_ids"
                            value={sendFormik.values.user_ids}
                            onChange={handleMultiUserChange}
                            onBlur={() => sendFormik.setFieldTouched("user_ids", true)}
                            style={{ minHeight: "120px" }}
                          // size={6}
                          // style={{ minHeight: "unset", height: "auto", overflowY: "auto" }}
                          >
                            {filteredUsers.length ? (
                              filteredUsers.map((user) => (
                                <option key={user?._id} value={user?._id}>
                                  {user?.name || "N/A"} ({user?.email || user?.phone_number || "N/A"})
                                </option>
                              ))
                            ) : (
                              <option disabled>{t("No users found")}</option>
                            )}
                          </select>
                          <small className="text-muted d-flex mt-1">
                            {t("Hold Ctrl/Cmd to select multiple users")}
                          </small>
                        </div>
                      </>
                    )}

                    {sendFormik.touched.user_ids && sendFormik.errors.user_ids ? (
                      <div className="col-md-12">
                        <span className="text-danger d-flex mb-2">{sendFormik.errors.user_ids}</span>
                      </div>
                    ) : null}
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("Title")} <span className="requirestar">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        className="form-control"
                        value={sendFormik.values.title}
                        onChange={sendFormik.handleChange}
                        onBlur={sendFormik.handleBlur}
                        placeholder={t("Enter title")}
                      />
                      {sendFormik.touched.title && sendFormik.errors.title ? (
                        <span className="text-danger d-flex">{sendFormik.errors.title}</span>
                      ) : null}
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">{t("Description")}</label>
                      <input
                        type="text"
                        name="description"
                        className="form-control"
                        value={sendFormik.values.description}
                        onChange={sendFormik.handleChange}
                        onBlur={sendFormik.handleBlur}
                        placeholder={t("Enter description")}
                      />
                    </div>
                    <div className="col-md-12">
                      <button
                        type="submit"
                        className="btn btn-primary mt-3 signbtn"
                        disabled={sendLoading}
                      >
                        {sendLoading ? t("Please wait...") : t("Send Notification")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* <div className="table-responsive">
              <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                <thead>
                  <tr>
                    <th>
                      <span>{t("list_heading_sno")}</span>
                    </th>
                    <th>
                      <div className="sorting_column">
                        <span>{t("Type")}</span>
                        <Sorting
                          sort={sorting}
                          handleSort={handleSort}
                          column="type"
                        />
                      </div>
                    </th>
                    <th>
                      <span>{t("Child")}</span>
                    </th>
                    <th>
                      <div className="sorting_column">
                        <span>{t("Created At")}</span>
                        <Sorting
                          sort={sorting}
                          handleSort={handleSort}
                          defaultSorting={defaultSorting}
                          column="createdAt"
                        />
                      </div>
                    </th>
                    <th>
                      <span>{t("Status")}</span>
                    </th>
                    <th className="action_head">{t("list_heading_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={10}>
                        <Loader />
                      </td>
                    </tr>
                  ) : list.length ? (
                    list.map((row, i) => (
                      <tr key={row?._id || i}>
                        <td>{(page - 1) * itemperpage + i + 1}</td>
                        <td>{row?.type || "N/A"}</td>
                        <td>{row?.child_id?.nick_name || row?.child?.nick_name || "N/A"}</td>
                        <td>{formatDateTime(row?.createdAt)}</td>
                        <td>
                          <span
                            className={`badge ${row?.read_by
                              ? "badge-success-light"
                              : "badge-danger-light"
                              }`}
                          >
                            {row?.read_by ? t("Read") : t("Unread")}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn ripple btn-main-primary signbtn"
                            disabled={row?.read_by}
                            onClick={() => markAsRead(row?._id)}
                          >
                            {t("Mark read")}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center">
                        {t("msg_no_records")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div> */}

            {/* <div id="example1_info" role="status" aria-live="polite">
              <b>
                {t("msg_total_records")} : {datalength || 0}
              </b>
            </div>

            {datalength > 0 ? (
              <CustomPagination
                datalength={datalength}
                itemperpage={itemperpage}
                currentPage={page}
                setPage={setPage}
                pageRoute={[
                  {
                    name: "Notifications",
                    path: "/admin/notification/list",
                  },
                ]}
              />
            ) : (
              ""
            )} */}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationTable;
